import React, { Suspense } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import { Loader } from '@jjj/components';
import { lazyWithReload } from '@jjj/move';
import { isSystemAdminRoleOrHR, UserRoles } from '../../helpers/userRoles';
import { isDayforce, isBench, isMaof } from '../../settings';
import PermissionRoute from './PermissionRoute';
import { PERMISSIONS } from '../../redux/permissions/constants';
import { canReadEngines } from '../../redux/permissions/helpers';
import LegalEntitiesList from '../admin/legalEntities/legalEntitiesList/legalEntitiesList';
import CreateLegalEntity from '../admin/legalEntities/createLegalEntity/createLegalEntity';

import { ROUTES } from './routes';

const Jobs = lazyWithReload(() => import('../jobs/jobs'));
const LandingPage = lazyWithReload(() => import('../landingPage/landingPage'));
const Leads = lazyWithReload(() => import('../leads/leads'));
const Companies = lazyWithReload(() => import('../clients/companies/companies'));
const Company = lazyWithReload(() => import('../clients/companies/company/company'));
const EditCompany = lazyWithReload(() => import('../clients/companies/company/editCompany'));
const EditContractSpecificAgreements = lazyWithReload(() =>
  import('../clients/companies/company/contractSpecificAgreements/editContractSpecificAgreements'),
);
const EditTalentConversionRule = lazyWithReload(() =>
  import('../clients/companies/company/talentConversionRule/editTalentConversionRule'),
);
const AddAccreditation = lazyWithReload(() =>
  import('../clients/components/accreditations/accreditation/addAccreditation/addAccreditation'),
);
const EditAccreditation = lazyWithReload(() =>
  import('../clients/components/accreditations/accreditation/editAccreditation/editAccreditation'),
);
const Units = lazyWithReload(() => import('../clients/units/units'));
const Unit = lazyWithReload(() => import('../clients/units/unit/unit'));
const EditClient = lazyWithReload(() => import('../clients/units/unit/editClient'));
const Invoice = lazyWithReload(() => import('../clients/units/unit/invoices/invoice/invoice'));
const AddNewPriceList = lazyWithReload(() => import('../clients/units/unit/priceList/addNewPriceList/addNewPriceList'));
const EditManager = lazyWithReload(() => import('../clients/units/unit/managers/manager/editManager'));
const ViewManager = lazyWithReload(() => import('../clients/units/unit/managers/manager/viewManager'));
const EditAgencies = lazyWithReload(() => import('../clients/units/unit/agencies/editAgencies/editAgencies'));
const PaymentDetails = lazyWithReload(() => import('../clients/units/unit/paymentMethods/details/paymentDetails'));
const SubUnits = lazyWithReload(() => import('../clients/subUnits/subUnits'));
const SubUnit = lazyWithReload(() => import('../clients/subUnits/subUnit/subUnit'));
const EditSubUnit = lazyWithReload(() => import('../clients/subUnits/subUnit/editSubUnit'));
const SubUnitInvoice = lazyWithReload(() => import('../clients/subUnits/subUnit/invoices/invoice/invoice'));
const EditSubUnitManager = lazyWithReload(() => import('../clients/subUnits/subUnit/managers/manager/editManager'));
const ViewSubUnitManager = lazyWithReload(() => import('../clients/subUnits/subUnit/managers/manager/viewManager'));
const PromoCodes = lazyWithReload(() => import('../admin/promoCodes/promoCodes'));
const PromoCode = lazyWithReload(() => import('../admin/promoCodes/promoCode/promoCode'));
const EditPromoCode = lazyWithReload(() => import('../admin/promoCodes/promoCode/editPromoCode'));
const UserList = lazyWithReload(() => import('../admin/users/userList/userList'));
const EditUser = lazyWithReload(() => import('../admin/users/user/editUser'));
const ViewUser = lazyWithReload(() => import('../admin/users/user/viewUser'));
const DistrictList = lazyWithReload(() => import('../admin/districts/districtList/districtList'));
const District = lazyWithReload(() => import('../admin/districts/district/district'));
const EditDistrict = lazyWithReload(() => import('../admin/districts/district/editDistrict/editDistrict'));
const SectorList = lazyWithReload(() => import('../admin/sectors/sectorList/sectorList'));
const Sector = lazyWithReload(() => import('../admin/sectors/sector/sector'));
const EditSector = lazyWithReload(() => import('../admin/sectors/sector/editSector'));
const PositionList = lazyWithReload(() => import('../admin/positions/positionList/positionList'));
const Position = lazyWithReload(() => import('../admin/positions/position/position'));
const AttentionRequiredList = lazyWithReload(() => import('../jobs/attentionRequiredList/attentionRequiredList'));
const Timesheets = lazyWithReload(() => import('../jobs/timesheetsList/timesheets'));
const Job = lazyWithReload(() => import('../jobs/job/job'));
const AddPromocode = lazyWithReload(() => import('../jobs/job/addPromocode/addPromocode'));
const EditBooking = lazyWithReload(() => import('../jobs/job/bookings/booking/editBooking/editBooking'));
const PoolList = lazyWithReload(() => import('../jobs/job/bookings/booking/pools/poolList'));
const EditBackups = lazyWithReload(() => import('../jobs/job/bookings/booking/editBackups/editBackups'));
const Talent = lazyWithReload(() => import('../talent/talent'));
const TalentProfile = lazyWithReload(() => import('../talent/talent/talent'));
const EditTalent = lazyWithReload(() => import('../talent/talent/editTalent'));
const Accreditation = lazyWithReload(() => import('../talent/talent/accreditations/accreditation/accreditation'));
const EditSuperannuation = lazyWithReload(() => import('../talent/talent/superannuations/editSuperannuation'));
const PastJobsList = lazyWithReload(() => import('../talent/talent/pastJobsList/pastJobsList'));
const PartnerList = lazyWithReload(() => import('../admin/partners/partnerList/partnerList'));
const Partner = lazyWithReload(() => import('../admin/partners/partner/partner'));
const EditPartner = lazyWithReload(() => import('../admin/partners/partner/editPartner'));
const DemoUsers = lazyWithReload(() => import('../admin/demoUsers/demoUsers'));
const DemoUser = lazyWithReload(() => import('../admin/demoUsers/demoUser/demoUser'));
const EditDemoUser = lazyWithReload(() => import('../admin/demoUsers/demoUser/editDemoUser'));
const Referrals = lazyWithReload(() => import('../admin/referrals/referrals'));
const TermsAndPrivacy = lazyWithReload(() => import('../admin/terms&privacy/terms&privacy'));
const Applicants = lazyWithReload(() => import('../applicants/applicants'));
const Applicant = lazyWithReload(() => import('../applicants/applicant/applicant'));
const ApplicantAccreditation = lazyWithReload(() =>
  import('../applicants/applicant/accreditations/accreditation/accreditation'),
);
const EditApplicantSuperannuation = lazyWithReload(() =>
  import('../applicants/applicant/superannuations/editSuperannuation'),
);
const Payroll = lazyWithReload(() => import('../admin/payroll/payroll'));
const DataUploads = lazyWithReload(() => import('../settings/dataUploads/dataUploads'));
const FlexAppOptions = lazyWithReload(() => import('../admin/flexAppOptions/flexAppOptions'));
const ClientFeedbackOptions = lazyWithReload(() => import('../admin/clientFeedbackOptions/clientFeedbackOptions'));
const Attires = lazyWithReload(() => import('../admin/attires/attires'));
const Attire = lazyWithReload(() => import('../admin/attires/attire/attire'));
const Shirt = lazyWithReload(() => import('../admin/attires/attire/attireGroups/shirt/shirt'));
const Pants = lazyWithReload(() => import('../admin/attires/attire/attireGroups/pants/pants'));
const EditAttire = lazyWithReload(() => import('../admin/attires/attire/editAttire'));
const Shoes = lazyWithReload(() => import('../admin/attires/attire/attireGroups/shoes/shoes'));
const AccreditationsList = lazyWithReload(() =>
  import('../admin/accreditations/accreditationsList/accreditationsList'),
);
const AdminAccreditation = lazyWithReload(() => import('../admin/accreditations/accreditation/accreditation'));
const EditAdminAccreditation = lazyWithReload(() => import('../admin/accreditations/accreditation/editAccreditation'));
const DeniedTalent = lazyWithReload(() => import('../admin/deniedTalent/deniedTalent'));
const TerminatedTalent = lazyWithReload(() => import('../admin/terminatedTalent/terminatedTalent'));
const Algorithm = lazyWithReload(() => import('../admin/algorithm/algorithm'));
const AutomationConfig = lazyWithReload(() => import('../admin/automationConfig/automationConfig'));
const CancellationPolicy = lazyWithReload(() => import('../admin/cancellationPolicy/cancellationPolicy'));
const Organization = lazyWithReload(() => import('../admin/organization/organization'));
const HolidayPricing = lazyWithReload(() => import('../admin/holidayPricing/holidayPricing'));
const RateEngineList = lazyWithReload(() => import('../admin/pricing/rateEngine/rateEngineList/rateEngineList'));
const ViewRateEngine = lazyWithReload(() => import('../admin/pricing/rateEngine/viewRateEngine/viewRateEngine'));
const EditRateEngine = lazyWithReload(() => import('../admin/pricing/rateEngine/editRateEngine/editRateEngine'));
const RateCards = lazyWithReload(() => import('../admin/pricing/rateCards/rateCards'));
const CreateRateCard = lazyWithReload(() => import('../admin/pricing/rateCards/createRateCard/createRateCard'));
const ViewRateCard = lazyWithReload(() => import('../admin/pricing/rateCards/viewRateCard/viewRateCard'));
const ReviewRateCard = lazyWithReload(() => import('../admin/pricing/rateCards/reviewRateCard/reviewRateCard'));
const RateSimulator = lazyWithReload(() => import('../admin/pricing/rateSimulator/rateSimulator'));
const PositionAvailability = lazyWithReload(() => import('../admin/pricing/positionAvailability/positionAvailability'));
const MarketsList = lazyWithReload(() => import('../admin/markets/marketsList/marketsList'));
const Market = lazyWithReload(() => import('../admin/markets/market/market'));
const Divisions = lazyWithReload(() => import('../clients/divisions/divisions'));
const Division = lazyWithReload(() => import('../clients/divisions/division/division'));
const EditDivision = lazyWithReload(() => import('../clients/divisions/division/editDivision'));
const Insights = lazyWithReload(() => import('../dashboard/insights/insights'));
const Sessions = lazyWithReload(() => import('../orientations/sessions/sessions'));
const AddSession = lazyWithReload(() => import('../orientations/addSession/addSession'));
const Session = lazyWithReload(() => import('../orientations/sessions/session/session'));
const OrganizationsList = lazyWithReload(() => import('../organizations/organizationsList/organizationsList'));
const EditOrganization = lazyWithReload(() => import('../organizations/organization/editOrganization'));
const ViewOrganization = lazyWithReload(() => import('../organizations/organization/viewOrganization'));
const Settings = lazyWithReload(() => import('../settings/settings'));
const EditProfile = lazyWithReload(() => import('../profile/editProfile'));
const NotificationsCenter = lazyWithReload(() => import('../notificationsCenter/notificationsCenter'));
const Forbidden = lazyWithReload(() => import('../forbidden/forbidden'));
const UserGuide = lazyWithReload(() => import('../userGuide/userGuide'));
const ActiveList = lazyWithReload(() => import('../agencies/activeList/activeList'));
const InviteList = lazyWithReload(() => import('../agencies/inviteList/inviteList'));
const RemovedList = lazyWithReload(() => import('../agencies/removedList/removedList'));
const InvoiceList = lazyWithReload(() => import('../agencies/invoiceList/invoiceList'));
const EditAgency = lazyWithReload(() => import('../agencies/editAgency/editAgency'));
const ViewAgency = lazyWithReload(() => import('../agencies/viewAgency/viewAgency'));
const UpdateAgencyContract = lazyWithReload(() => import('../agencies/updateAgencyContract/updateAgencyContract'));
const ViewAgencyContractV2 = lazyWithReload(() =>
  import('../agencies/agencyContractsV2/viewAgencyContract/viewAgencyContract'),
);
const EditAgencyContractV2 = lazyWithReload(() =>
  import('../agencies/agencyContractsV2/editAgencyContract/editAgencyContract'),
);
const NotFoundPage = lazyWithReload(() => import('../notFoundPage/notFoundPage'));
const LoadingLoader = lazyWithReload(() => import('../onBehalfOf/loadingLoader'));
const ViewAgencyWorker = lazyWithReload(() => import('../agencyWorkers/viewAgencyWorker/viewAgencyWorker'));
const Shifts = lazyWithReload(() => import('../agencyWorkers/viewAgencyWorker/shifts/shifts'));
const ChecklistTaskProgress = lazyWithReload(() =>
  import('../agencyWorkers/viewAgencyWorker/checklists/checklistTaskProgress/checklistTaskProgress'),
);
const ConvertToPermanent = lazyWithReload(() => import('../agencyWorkers/convertToPermanent/convertToPermanent'));
const AgencyWorkersList = lazyWithReload(() => import('../agencyWorkers/agencyWorkersList/agencyWorkersList'));
const Checklists = lazyWithReload(() => import('../admin/checklists/checklists'));
const ChecklistEditor = lazyWithReload(() => import('../admin/checklists/editor/checklistsEditor'));
const TaskReview = lazyWithReload(() => import('../agencyWorkers/viewAgencyWorker/checklists/taskReview'));

export { ROUTES };

const AGENCY_WORKERS_MANAGEMENT_ROUTES = [
  ROUTES.AGENCY_WORKERS,
  ROUTES.AGENCY_WORKERS_CHECKLIST,
  ROUTES.AGENCY_WORKERS_TASK_REVIEW,
  ROUTES.AGENCY_WORKERS_SHIFTS,
  ROUTES.AGENCY_WORKERS_LIST,
  ROUTES.AGENCY_WORKERS_CONVERT_TO_PERMANENT,
];

const INTERNAL_WORKERS_MANAGEMENT_ROUTES = [
  ROUTES.INTERVIEWS,
  ROUTES.APPLICANT_INTERVIEWS,
  ROUTES.EDIT_APPLICANT_INTERVIEWS,
  ROUTES.APPLICANT_LIST,
  ROUTES.VIEW_APPLICANT,
  ROUTES.EDIT_APPLICANT,
  ROUTES.APPLICANT_ACCREDITATION,
  ROUTES.APPLICANT_SUPERANNUATION,
  ROUTES.TALENT_LIST,
  ROUTES.VIEW_TALENT,
  ROUTES.EDIT_TALENT,
  ROUTES.TALENT_ACCREDITATION,
  ROUTES.TALENT_SUPERANNUATION,
  ROUTES.EDIT_TALENT_SUPERANNUATION,
  ROUTES.TALENT_PAST_JOBS,
  ROUTES.DENIED_TALENT_LIST,
  ROUTES.TERMINATED_TALENT_LIST,
  ROUTES.VIEW_DENIED_TALENT,
  ROUTES.VIEW_TERMINATED_TALENT,
  ROUTES.EDIT_DENIED_TALENT,
  ROUTES.EDIT_TERMINATED_TALENT,
];

const BASIC_ACCESS = [
  ROUTES.FORBIDDEN,
  ROUTES.NOTIFICATIONS,
  ROUTES.NOTIFICATIONS_ARCHIVE,
  ROUTES.NO_MATCH,
  ROUTES.PAGE_NOT_FOUND,
  ROUTES.PROFILE,
  ROUTES.SETTINGS,
  ROUTES.USER_GUIDE,
  ROUTES.USER_GUIDE_KEY,
];

const PROCUREMENT_ACCESS = [
  ROUTES.AGENCIES,
  ROUTES.AGENCIES_ACTIVE_LIST,
  ROUTES.AGENCIES_INVITE_LIST,
  ROUTES.AGENCIES_REMOVE_LIST,
  ROUTES.AGENCY_CONTRACT_UPDATE,
  ROUTES.AGENCIES_ADD,
  ROUTES.AGENCIES_EDIT,
  ROUTES.AGENCIES_INVITE,
  ROUTES.AGENCIES_ACTIVE,
  ROUTES.AGENCY_CONTRACT_V2_VIEW,
  ROUTES.AGENCY_CONTRACT_V2_CREATE,
  ROUTES.AGENCY_CONTRACT_V2_EDIT,
  ROUTES.AGENCY_CONTRACT_V2_SCOPE,
  ROUTES.DASHBOARD_INSIGHTS,
  ...BASIC_ACCESS,
];

const pricingRoute = (permission, redirectTo, element) => (
  <PermissionRoute permission={permission} redirectTo={redirectTo}>
    {element}
  </PermissionRoute>
);

// The editor needs both permissions: it reads versions and engines under the engine read check but fetches the
// rule library under `engines.create`. Gating on write alone strands a read-less admin on an error screen.
const pricingEditorRoute = element =>
  pricingRoute(
    canReadEngines,
    '/forbidden',
    pricingRoute(PERMISSIONS.pricing_settings.engines.create, `/${ROUTES.RATE_ENGINE_LIST}`, element),
  );

const routes = [
  {
    path: '/',
    element: <Jobs />,
  },
  {
    path: ROUTES.DASHBOARD,
    element: <LandingPage />,
  },
  {
    path: 'leads',
    element: <Leads />,
  },
  {
    path: 'companies',
    element: <Companies />,
  },
  {
    path: 'companies/:id',
    element: <Company />,
  },
  {
    path: 'companies/:id/edit',
    element: <EditCompany />,
  },
  {
    path: 'companies/:id/rules',
    element: <EditContractSpecificAgreements />,
  },
  {
    path: 'companies/:id/rules/:company_pricing_agreement_id',
    element: <EditContractSpecificAgreements />,
  },
  {
    path: 'companies/:id/editConversionRule',
    element: <EditTalentConversionRule />,
  },
  {
    path: 'companies/:id/accreditations/add',
    element: <AddAccreditation clientType="companies" />,
  },
  {
    path: 'companies/:id/accreditations/:accreditationId/edit',
    element: <EditAccreditation clientType="companies" />,
  },
  {
    path: 'units',
    element: <Units />,
  },
  {
    path: 'units/:id',
    element: <Unit />,
  },
  {
    path: 'units/:id/edit',
    element: <EditClient />,
  },
  {
    path: 'units/:id/invoices/:invoiceId',
    element: <Invoice />,
  },
  {
    path: 'units/:id/price-lists/add',
    element: <AddNewPriceList />,
  },
  {
    path: 'units/:id/managers/:managerId/edit',
    element: <EditManager />,
  },
  {
    path: 'units/:id/managers/add',
    element: <EditManager />,
  },
  {
    path: 'units/:id/managers/:managerId',
    element: <ViewManager />,
  },
  {
    path: 'units/:id/accreditations/add',
    element: <AddAccreditation clientType="units" />,
  },
  {
    path: 'units/:id/accreditations/:accreditationId/edit',
    element: <EditAccreditation clientType="units" />,
  },
  {
    path: 'units/:id/editAgencies',
    element: <EditAgencies />,
  },
  {
    path: 'units/:id/paymentDetails',
    element: <PaymentDetails />,
  },
  {
    path: 'subUnits',
    element: <SubUnits />,
  },
  {
    path: 'subUnits/:id',
    element: <SubUnit />,
  },
  {
    path: 'subUnits/:id/edit',
    element: <EditSubUnit />,
  },
  {
    path: 'subUnits/:id/invoices/:invoiceId',
    element: <SubUnitInvoice />,
  },
  {
    path: 'subUnits/:id/managers/:managerId/edit',
    element: <EditSubUnitManager />,
  },
  {
    path: 'subUnits/:id/managers/add',
    element: <EditSubUnitManager />,
  },
  {
    path: 'subUnits/:id/price-lists/add',
    element: <AddNewPriceList />,
  },
  {
    path: 'subUnits/:id/managers/:managerId',
    element: <ViewSubUnitManager />,
  },
  {
    path: 'subUnits/:id/accreditations/add',
    element: <AddAccreditation clientType="subunits" />,
  },
  {
    path: 'subUnits/:id/accreditations/:accreditationId/edit',
    element: <EditAccreditation clientType="subunits" />,
  },
  {
    path: 'subUnits/:id/paymentDetails',
    element: <PaymentDetails />,
  },
  {
    path: 'promoCodes',
    element: <PromoCodes />,
  },
  {
    path: 'promoCodes/:id',
    element: <PromoCode />,
  },
  {
    path: 'promoCodes/:id/edit',
    element: <EditPromoCode />,
  },
  {
    path: 'users',
    element: <UserList />,
  },
  {
    path: 'users/add',
    element: <EditUser />,
  },
  {
    path: 'users/:id',
    element: <ViewUser />,
  },
  {
    path: 'users/:id/edit',
    element: <EditUser />,
  },
  {
    path: 'districts',
    element: <DistrictList />,
  },
  {
    path: 'districts/:id',
    element: <District />,
  },
  {
    path: 'districts/:id/edit',
    element: <EditDistrict />,
  },
  {
    path: 'sectors',
    element: <SectorList />,
  },
  {
    path: 'sectors/:id',
    element: <Sector />,
  },
  {
    path: 'sectors/:id/edit',
    element: <EditSector />,
  },
  {
    path: 'positions',
    element: <PositionList />,
  },
  {
    path: 'positions/:id',
    element: <Position />,
  },
  {
    path: 'jobs',
    element: <Jobs />,
  },
  {
    path: 'jobs/multiDay',
    element: <Jobs />,
  },
  {
    path: 'attentionRequired',
    element: <AttentionRequiredList />,
  },
  {
    path: 'timesheets',
    element: <Timesheets />,
  },
  {
    path: 'jobs/:id',
    element: <Job />,
  },
  {
    path: 'jobs/multiDay/:id',
    element: <Job />,
  },
  {
    path: 'jobs/:id/addPromocode',
    element: <AddPromocode />,
  },
  {
    path: 'jobs/multiDay/:id/addPromocode',
    element: <AddPromocode />,
  },
  {
    path: 'jobs/:id/addGig',
    element: <EditBooking />,
  },
  {
    path: 'jobs/multiDay/:id/addGig',
    element: <EditBooking />,
  },
  {
    path: 'jobs/:id/bookings/:bookingId/pool',
    element: <PoolList />,
  },
  {
    path: 'jobs/:id/bookings/:bookingId/editBackups',
    element: <EditBackups />,
  },
  {
    path: 'jobs/multiDay/:id/bookings/:bookingId/editBackups',
    element: <EditBackups />,
  },
  {
    path: 'jobs/:id/bookings/:bookingId/editBooking',
    element: <EditBooking />,
  },
  {
    path: 'jobs/multiDay/:id/bookings/:bookingId/editBooking',
    element: <EditBooking />,
  },
  {
    path: 'talent',
    element: <Talent />,
  },
  {
    path: 'talent/:id',
    element: <TalentProfile />,
  },
  {
    path: 'talent/:id/edit',
    element: <EditTalent />,
  },
  {
    path: 'talent/:id/talentAccreditations/:talentAccreditationId',
    element: <Accreditation />,
  },
  {
    path: 'talent/:id/editSuperannuation',
    element: <EditSuperannuation />,
  },
  {
    path: 'talent/:id/pastJobs',
    element: <PastJobsList />,
  },
  {
    path: 'partners',
    element: <PartnerList />,
  },
  {
    path: 'partners/:id',
    element: <Partner />,
  },
  {
    path: 'partners/:id/edit',
    element: <EditPartner />,
  },
  {
    path: 'demoUsers',
    element: <DemoUsers />,
  },
  {
    path: 'demoUsers/:id',
    element: <DemoUser />,
  },
  {
    path: 'demoUsers/:id/edit',
    element: <EditDemoUser />,
  },
  {
    path: 'referrals',
    element: <Referrals />,
  },
  {
    path: 'termsAndPrivacy',
    element: <TermsAndPrivacy />,
  },
  {
    path: 'interviews',
    element: <Applicants />,
  },
  {
    path: 'interviews/:id',
    element: <Applicant />,
  },
  {
    path: 'interviews/:id/edit',
    element: <Applicant />,
  },
  {
    path: 'applicants',
    element: <Applicants />,
  },
  {
    path: 'applicants/:id',
    element: <Applicant />,
  },
  {
    path: 'applicants/:id/edit',
    element: <Applicant />,
  },
  {
    path: 'applicants/:id/applicantAccreditations/:applicantAccreditationId',
    element: <ApplicantAccreditation />,
  },
  {
    path: 'applicants/:id/editSuperannuation',
    element: <EditApplicantSuperannuation />,
  },
  {
    path: 'payroll',
    element: <Payroll />,
  },
  {
    path: ROUTES.CHECKLISTS,
    element: (
      <PermissionRoute permission={PERMISSIONS.checklists.index} redirectTo="/forbidden">
        <Checklists />
      </PermissionRoute>
    ),
  },
  {
    path: ROUTES.CREATE_CHECKLIST,
    element: (
      <PermissionRoute permission={PERMISSIONS.checklists.create} redirectTo="/forbidden">
        <ChecklistEditor />
      </PermissionRoute>
    ),
  },
  {
    path: ROUTES.EDIT_CHECKLIST,
    element: (
      <PermissionRoute permission={PERMISSIONS.checklists.update} redirectTo="/forbidden">
        <ChecklistEditor />
      </PermissionRoute>
    ),
  },
  {
    path: ROUTES.DATA_UPLOADS,
    element: (
      <PermissionRoute permission={PERMISSIONS.bulk_upload.index} redirectTo="/forbidden">
        <DataUploads />
      </PermissionRoute>
    ),
  },
  {
    path: ROUTES.LEGAL_ENTITIES,
    element: (
      <PermissionRoute permission={PERMISSIONS.legal_entity.index} redirectTo="/forbidden">
        <LegalEntitiesList />
      </PermissionRoute>
    ),
  },
  {
    path: ROUTES.LEGAL_ENTITIES_CREATE,
    element: (
      <PermissionRoute permission={PERMISSIONS.legal_entity.create} redirectTo="/legal_entities">
        <CreateLegalEntity />
      </PermissionRoute>
    ),
  },
  {
    path: ROUTES.LEGAL_ENTITIES_EDIT,
    element: (
      <PermissionRoute permission={PERMISSIONS.legal_entity.update} redirectTo="/legalEntities">
        <CreateLegalEntity />
      </PermissionRoute>
    ),
  },
  {
    path: 'flexAppOptions',
    element: <FlexAppOptions />,
  },
  {
    path: 'clientFeedbackOptions',
    element: <ClientFeedbackOptions />,
  },
  {
    path: 'attires',
    element: <Attires />,
  },
  {
    path: 'attires/:id/edit',
    element: <Attire />,
  },
  {
    path: 'attires/shirts/:id',
    element: <Shirt />,
  },
  {
    path: 'attires/pants/:id',
    element: <Pants />,
  },
  {
    path: 'attires/pants/:id/edit',
    element: <EditAttire />,
  },
  {
    path: 'attires/shoes/:id',
    element: <Shoes />,
  },
  {
    path: 'accreditations',
    element: <AccreditationsList />,
  },
  {
    path: 'accreditations/:id',
    element: <AdminAccreditation />,
  },
  {
    path: 'accreditations/:id/edit',
    element: <EditAdminAccreditation />,
  },
  {
    path: 'denied',
    element: <DeniedTalent />,
  },
  {
    path: 'terminated',
    element: <TerminatedTalent />,
  },
  {
    path: 'denied/:id',
    element: <Applicant />,
  },
  {
    path: 'terminated/:id',
    element: <TalentProfile />,
  },
  {
    path: 'denied/:id/edit',
    element: <Applicant />,
  },
  {
    path: 'terminated/:id/edit',
    element: <EditTalent />,
  },
  {
    path: 'algorithm',
    element: <Algorithm />,
  },
  {
    path: 'automationConfig',
    element: <AutomationConfig />,
  },
  {
    path: 'cancellationPolicy',
    element: <CancellationPolicy />,
  },
  {
    path: 'organization',
    element: <Organization />,
  },
  {
    path: 'holidayPricing',
    element: <HolidayPricing />,
  },
  {
    path: ROUTES.RATE_ENGINE_LIST,
    element: pricingRoute(canReadEngines, '/forbidden', <RateEngineList />),
  },
  {
    path: ROUTES.RATE_ENGINE_CREATE,
    element: pricingEditorRoute(<EditRateEngine mode="create" />),
  },
  {
    path: ROUTES.RATE_ENGINE_VIEW,
    element: pricingRoute(canReadEngines, '/forbidden', <ViewRateEngine />),
  },
  {
    path: ROUTES.RATE_ENGINE_VERSION_CREATE,
    element: pricingEditorRoute(<EditRateEngine mode="newVersion" />),
  },
  {
    path: ROUTES.RATE_ENGINE_EDIT,
    element: pricingEditorRoute(<EditRateEngine mode="edit" />),
  },
  {
    path: ROUTES.RATE_CARDS,
    element: pricingRoute(PERMISSIONS.pricing_settings.index, '/forbidden', <RateCards />),
  },
  {
    path: ROUTES.RATE_CARDS_CREATE,
    element: (
      // A write from end to end — it creates a card and uploads to it — so `index` is not enough.
      <PermissionRoute permission={PERMISSIONS.pricing_settings.rate_cards.create} redirectTo="/forbidden">
        <CreateRateCard />
      </PermissionRoute>
    ),
  },
  {
    path: ROUTES.RATE_CARDS_REVIEW,
    element: (
      // Reviewing ends in a publish, and re-importing is an upload; both need the write permission.
      <PermissionRoute permission={PERMISSIONS.pricing_settings.rate_cards.create} redirectTo="/forbidden">
        <ReviewRateCard />
      </PermissionRoute>
    ),
  },
  {
    path: ROUTES.RATE_CARDS_VIEW,
    element: (
      <PermissionRoute permission={PERMISSIONS.pricing_settings.index} redirectTo="/forbidden">
        <ViewRateCard />
      </PermissionRoute>
    ),
  },
  {
    path: ROUTES.RATE_SIMULATOR,
    element: pricingRoute(PERMISSIONS.pricing_settings.index, '/forbidden', <RateSimulator />),
  },
  {
    path: ROUTES.POSITION_AVAILABILITY,
    element: pricingRoute(PERMISSIONS.pricing_settings.index, '/forbidden', <PositionAvailability />),
  },
  {
    path: 'markets',
    element: <MarketsList />,
  },
  {
    path: 'markets/:id',
    element: <Market />,
  },
  {
    path: 'divisions',
    element: <Divisions />,
  },
  {
    path: 'divisions/:id',
    element: <Division />,
  },
  {
    path: 'divisions/:id/edit',
    element: <EditDivision />,
  },
  {
    path: 'divisions/:id/accreditations/add',
    element: <AddAccreditation clientType="divisions" />,
  },
  {
    path: 'divisions/:id/accreditations/:accreditationId/edit',
    element: <EditAccreditation clientType="divisions" />,
  },
  {
    path: ROUTES.DASHBOARD_INSIGHTS,
    element: <Insights />,
  },
  {
    path: 'sessions',
    element: <Sessions />,
  },
  {
    path: 'sessions/add',
    element: <AddSession />,
  },
  {
    path: 'sessions/:id',
    element: <Sessions />,
  },
  {
    path: 'sessions/:orientation_id/session/:session_id',
    element: <Session />,
  },
  {
    path: 'sessions/:orientation_id/settings',
    element: <Session />,
  },
  {
    path: ROUTES.ORGANIZATIONS,
    element: <OrganizationsList />,
  },
  {
    path: ROUTES.ORGANIZATION_CREATE,
    element: <EditOrganization />,
  },
  {
    path: ROUTES.ORGANIZATION_UPDATE,
    element: <EditOrganization />,
  },
  {
    path: ROUTES.ORGANIZATION_VIEW,
    element: <ViewOrganization />,
  },
  {
    path: ROUTES.SETTINGS,
    element: <Settings />,
  },
  {
    path: ROUTES.PROFILE,
    element: <EditProfile />,
  },
  {
    path: ROUTES.NOTIFICATIONS,
    element: <NotificationsCenter />,
  },
  {
    path: ROUTES.NOTIFICATIONS_ARCHIVE,
    element: <NotificationsCenter />,
  },
  {
    path: ROUTES.FORBIDDEN,
    element: <Forbidden />,
  },
  {
    path: ROUTES.USER_GUIDE,
    element: <UserGuide />,
  },
  {
    path: ROUTES.USER_GUIDE_KEY,
    element: <UserGuide />,
  },
  {
    path: ROUTES.AGENCIES,
    element: <ActiveList />,
  },
  {
    path: ROUTES.AGENCIES_ACTIVE_LIST,
    element: <ActiveList />,
  },
  {
    path: ROUTES.AGENCIES_INVITE_LIST,
    element: <InviteList />,
  },
  {
    path: ROUTES.AGENCIES_REMOVE_LIST,
    element: <RemovedList />,
  },
  {
    path: ROUTES.AGENCIES_INVOICE_LIST,
    element: <InvoiceList />,
  },
  {
    path: ROUTES.AGENCIES_CONSOLIDATED_INVOICE,
    element: <InvoiceList />,
  },
  {
    path: ROUTES.AGENCIES_ADD,
    element: <EditAgency />,
  },
  {
    path: ROUTES.AGENCIES_EDIT,
    element: <EditAgency />,
  },
  {
    path: ROUTES.AGENCIES_INVITE,
    element: <ViewAgency />,
  },
  {
    path: ROUTES.AGENCIES_ACTIVE,
    element: <ViewAgency />,
  },
  {
    path: ROUTES.AGENCY_CONTRACT_UPDATE,
    element: <UpdateAgencyContract />,
  },
  {
    path: ROUTES.AGENCY_CONTRACT_V2_CREATE,
    element: <EditAgencyContractV2 mode="create" />,
  },
  {
    path: ROUTES.AGENCY_CONTRACT_V2_VIEW,
    element: <ViewAgencyContractV2 />,
  },
  {
    path: ROUTES.AGENCY_CONTRACT_V2_EDIT,
    element: <EditAgencyContractV2 mode="edit" />,
  },
  {
    path: ROUTES.AGENCY_CONTRACT_V2_SCOPE,
    element: <EditAgencyContractV2 mode="scope" />,
  },
  {
    path: ROUTES.PAGE_NOT_FOUND,
    element: <NotFoundPage />,
  },
  {
    path: ROUTES.ADMIN_SIGN_IN,
    element: <LoadingLoader />,
  },
  {
    path: ROUTES.AGENCY_WORKERS_CONVERT_TO_PERMANENT,
    element: <ConvertToPermanent />,
  },
  {
    path: ROUTES.AGENCY_WORKERS,
    element: <ViewAgencyWorker />,
  },
  {
    path: ROUTES.AGENCY_WORKERS_CHECKLIST,
    element: (
      <PermissionRoute permission={PERMISSIONS.checklists.index} redirectTo="/forbidden">
        <ChecklistTaskProgress />,
      </PermissionRoute>
    ),
  },
  {
    path: ROUTES.AGENCY_WORKERS_SHIFTS,
    element: <Shifts />,
  },
  {
    path: ROUTES.AGENCY_WORKERS_LIST,
    element: <AgencyWorkersList />,
  },
  {
    path: ROUTES.NO_MATCH,
    element: <Navigate to="404" replace />,
  },
  {
    path: ROUTES.AGENCY_WORKERS_TASK_REVIEW,
    element: (
      <PermissionRoute permission={PERMISSIONS.checklists.index} redirectTo="/forbidden">
        <TaskReview />
      </PermissionRoute>
    ),
  },
];

function checkSpecificRoleAccess(route, role) {
  switch (role) {
    case UserRoles.PROCUREMENT:
      return PROCUREMENT_ACCESS.includes(route);
    case UserRoles.CUSTOMER_SERVICE:
    case UserRoles.ADMIN:
    case UserRoles.SYSTEM_ADMIN:
    case UserRoles.ACCOUNT_MANAGER:
    case UserRoles.MARKETING:
    case UserRoles.FINANCE:
    case UserRoles.RECRUITER:
    case UserRoles.CUSTOMER_SERVICE_SUPERVISOR:
    case UserRoles.SALES:
      return true;
    default:
      return false;
  }
}

function checkSpecificRouteAccess(route, role) {
  switch (route) {
    case ROUTES.ORGANIZATIONS:
    case ROUTES.ORGANIZATION_CREATE:
    case ROUTES.ORGANIZATION_UPDATE:
    case ROUTES.ORGANIZATION_VIEW:
      return [UserRoles.SYSTEM_ADMIN].includes(role);
    default:
      return true;
  }
}

const DASH_ONLY_ROUTES = [
  ROUTES.ORGANIZATIONS,
  ROUTES.ORGANIZATION_CREATE,
  ROUTES.ORGANIZATION_UPDATE,
  ROUTES.ORGANIZATION_VIEW,
];
const MAOF_ONLY_ROUTES = [];

function checkOrgAccess(route) {
  if (isBench()) {
    return ![...DASH_ONLY_ROUTES, ...MAOF_ONLY_ROUTES].includes(route);
  }

  if (isDayforce()) {
    return !MAOF_ONLY_ROUTES.includes(route);
  }

  if (isMaof()) {
    return !DASH_ONLY_ROUTES.includes(route);
  }

  return true;
}

function checkWorkersManagementAccess(
  route,
  isAgencyWorkersManagementEnabled,
  isInternalWorkersManagementEnabled,
  isConversionEnabled,
  role,
) {
  const canViewAgencyWorkers = (isAgencyWorkersManagementEnabled || isConversionEnabled) && isSystemAdminRoleOrHR(role);

  if (AGENCY_WORKERS_MANAGEMENT_ROUTES.includes(route)) {
    return canViewAgencyWorkers;
  }

  if (INTERNAL_WORKERS_MANAGEMENT_ROUTES.includes(route)) {
    return isInternalWorkersManagementEnabled && !canViewAgencyWorkers;
  }

  return true;
}

export default function AppRouter({ role, featureFlags }) {
  const isAgencyWorkersManagementEnabled = featureFlags?.agency_workers_management_enabled;
  const isInternalWorkersManagementEnabled = featureFlags?.internal_workers_management_enabled;
  const isConversionEnabled = featureFlags?.dayforce_worker_identity_conversion_enabled;

  if (featureFlags == null) {
    return (
      <Routes>
        <Route path="*" element={<Loader />} />
      </Routes>
    );
  }

  return (
    <Suspense fallback={<Loader />}>
      <Routes>
        {routes
          .filter(route => checkSpecificRoleAccess(route.path, role))
          .filter(route => checkSpecificRouteAccess(route.path, role))
          .filter(route => checkOrgAccess(route.path))
          .filter(route =>
            checkWorkersManagementAccess(
              route.path,
              isAgencyWorkersManagementEnabled,
              isInternalWorkersManagementEnabled,
              isConversionEnabled,
              role,
            ),
          )
          .map(singleRoute => {
            const { path, ...otherProps } = singleRoute;

            return <Route key={path} path={path} {...otherProps} />;
          })}
      </Routes>
    </Suspense>
  );
}
